import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Check if Supabase credentials are available
const hasSupabaseCredentials = supabaseUrl && supabaseAnonKey && supabaseUrl !== 'undefined' && supabaseAnonKey !== 'undefined';

export const supabase = hasSupabaseCredentials ? createClient(supabaseUrl, supabaseAnonKey) : null;

// Types for the new database structure
export interface Event {
  id: string;
  event_name: string;
  event_type: string;
  price: number;
  max_capacity: number;
  created_at: string;
}

export interface EventAvailability extends Event {
  current_registrations: number;
  available_spots: number;
  is_full: boolean;
}

export interface ContestantRegistration {
  id?: string;
  event_id: string;
  first_name: string;
  last_name: string;
  email: string;
  address: string;
  postal_code: string;
  phone_number: string;
  birth_date: string;
  gender: 'Male' | 'Female';
  emergency_contact_name: string;
  emergency_contact_phone: string;
  emergency_contact_relation: string;
  age_verification: boolean;
  interesting_fact: string;
  full_signature: string;
  waiver_accepted: boolean;
  waiver_required?: boolean;
  payment_status?: string;
  confirmation_number?: string;
  created_at?: string;
  // Team roping specific fields
  partner_name?: string;
  partner_email?: string;
  partner_phone?: string;
  team_entry_id?: string;
  is_team_event?: boolean;
}

// Mock event data as fallback when Supabase is not available
const mockEvents: EventAvailability[] = [
  // July 3rd events
  { id: '1', event_name: 'Bull Riding (at beginning of rodeo)', event_type: 'July 3rd', price: 100.00, max_capacity: 10, current_registrations: 3, available_spots: 7, is_full: false, created_at: '2026-01-01' },
  { id: '2', event_name: 'Tie Down Roping', event_type: 'July 3rd', price: 85.00, max_capacity: 10, current_registrations: 5, available_spots: 5, is_full: false, created_at: '2026-01-01' },
  { id: '3', event_name: 'Bareback', event_type: 'July 3rd', price: 85.00, max_capacity: 10, current_registrations: 2, available_spots: 8, is_full: false, created_at: '2026-01-01' },
  { id: '4', event_name: 'Breakaway', event_type: 'July 3rd', price: 85.00, max_capacity: 10, current_registrations: 4, available_spots: 6, is_full: false, created_at: '2026-01-01' },
  { id: '5', event_name: 'Saddle Bronc', event_type: 'July 3rd', price: 85.00, max_capacity: 10, current_registrations: 1, available_spots: 9, is_full: false, created_at: '2026-01-01' },
  { id: '6', event_name: 'Steer Wrestling', event_type: 'July 3rd', price: 85.00, max_capacity: 10, current_registrations: 6, available_spots: 4, is_full: false, created_at: '2026-01-01' },
  { id: '7', event_name: 'Ranch Bronc', event_type: 'July 3rd', price: 50.00, max_capacity: 10, current_registrations: 3, available_spots: 7, is_full: false, created_at: '2026-01-01' },
  { id: '8', event_name: 'Team Roping', event_type: 'July 3rd', price: 85.00, max_capacity: 20, current_registrations: 8, available_spots: 12, is_full: false, created_at: '2026-01-01' },
  { id: '9', event_name: 'Barrel Racing', event_type: 'July 3rd', price: 85.00, max_capacity: 10, current_registrations: 7, available_spots: 3, is_full: false, created_at: '2026-01-01' },
  { id: '10', event_name: 'Junior Barrel Racing', event_type: 'July 3rd', price: 25.00, max_capacity: 10, current_registrations: 4, available_spots: 6, is_full: false, created_at: '2026-01-01' },
  { id: '11', event_name: 'Bull Riding (at end of rodeo)', event_type: 'July 3rd', price: 100.00, max_capacity: 10, current_registrations: 10, available_spots: 0, is_full: true, created_at: '2026-01-01' },

  // July 4th events
  { id: '12', event_name: 'Bull Riding (at beginning of rodeo)', event_type: 'July 4th', price: 100.00, max_capacity: 10, current_registrations: 2, available_spots: 8, is_full: false, created_at: '2026-01-01' },
  { id: '13', event_name: 'Tie Down Roping', event_type: 'July 4th', price: 85.00, max_capacity: 10, current_registrations: 3, available_spots: 7, is_full: false, created_at: '2026-01-01' },
  { id: '14', event_name: 'Bareback', event_type: 'July 4th', price: 85.00, max_capacity: 10, current_registrations: 5, available_spots: 5, is_full: false, created_at: '2026-01-01' },
  { id: '15', event_name: 'Breakaway', event_type: 'July 4th', price: 85.00, max_capacity: 10, current_registrations: 1, available_spots: 9, is_full: false, created_at: '2026-01-01' },
  { id: '16', event_name: 'Saddle Bronc', event_type: 'July 4th', price: 85.00, max_capacity: 10, current_registrations: 4, available_spots: 6, is_full: false, created_at: '2026-01-01' },
  { id: '17', event_name: 'Steer Wrestling', event_type: 'July 4th', price: 85.00, max_capacity: 10, current_registrations: 2, available_spots: 8, is_full: false, created_at: '2026-01-01' },
  { id: '18', event_name: 'Ranch Bronc', event_type: 'July 4th', price: 50.00, max_capacity: 10, current_registrations: 1, available_spots: 9, is_full: false, created_at: '2026-01-01' },
  { id: '19', event_name: 'Team Roping', event_type: 'July 4th', price: 85.00, max_capacity: 20, current_registrations: 6, available_spots: 14, is_full: false, created_at: '2026-01-01' },
  { id: '20', event_name: 'Barrel Racing', event_type: 'July 4th', price: 85.00, max_capacity: 10, current_registrations: 8, available_spots: 2, is_full: false, created_at: '2026-01-01' },
  { id: '21', event_name: 'Junior Barrels', event_type: 'July 4th', price: 25.00, max_capacity: 10, current_registrations: 3, available_spots: 7, is_full: false, created_at: '2026-01-01' },
  { id: '22', event_name: 'Bull Riding (at end of rodeo)', event_type: 'July 4th', price: 100.00, max_capacity: 10, current_registrations: 6, available_spots: 4, is_full: false, created_at: '2026-01-01' },

  // Slack Rodeo 7AM events (unlimited capacity)
  { id: '23', event_name: 'Tie Down Roping', event_type: 'Slack Rodeo 7AM', price: 85.00, max_capacity: 999999, current_registrations: 12, available_spots: 999999, is_full: false, created_at: '2026-01-01' },
  { id: '24', event_name: 'Breakaway', event_type: 'Slack Rodeo 7AM', price: 85.00, max_capacity: 999999, current_registrations: 8, available_spots: 999999, is_full: false, created_at: '2026-01-01' },
  { id: '25', event_name: 'Steer Wrestling', event_type: 'Slack Rodeo 7AM', price: 85.00, max_capacity: 999999, current_registrations: 5, available_spots: 999999, is_full: false, created_at: '2026-01-01' },
  { id: '26', event_name: 'Team Roping', event_type: 'Slack Rodeo 7AM', price: 85.00, max_capacity: 999999, current_registrations: 10, available_spots: 999999, is_full: false, created_at: '2026-01-01' },
  { id: '27', event_name: 'Barrel Racing', event_type: 'Slack Rodeo 7AM', price: 85.00, max_capacity: 999999, current_registrations: 15, available_spots: 999999, is_full: false, created_at: '2026-01-01' },
];

// Test Supabase connection
const testSupabaseConnection = async (): Promise<boolean> => {
  if (!supabase) {
    console.log('Supabase client not initialized - missing credentials');
    return false;
  }

  try {
    // Simple test query to check connection
    const { data, error } = await supabase
      .from('events')
      .select('id')
      .limit(1);
    
    if (error) {
      console.log('Supabase connection test failed:', error.message);
      return false;
    }
    
    console.log('Supabase connection test successful');
    return true;
  } catch (err) {
    console.log('Supabase connection test failed with exception:', err);
    return false;
  }
};

// Helper functions
export const getAvailableEvents = async (): Promise<EventAvailability[]> => {
  console.log('Fetching available events...');
  
  // If no Supabase credentials, immediately return mock data
  if (!hasSupabaseCredentials) {
    console.log('No Supabase credentials found, using mock data');
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 500));
    return mockEvents;
  }
  
  // Test connection first
  const connectionWorking = await testSupabaseConnection();
  
  if (!connectionWorking) {
    console.log('Using mock data due to Supabase connection issues');
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 500));
    return mockEvents;
  }
  
  try {
    console.log('Supabase connection successful, fetching event_availability...');
    
    // Try to fetch from the view
    const { data, error } = await supabase!
      .from('event_availability')
      .select('*')
      .order('event_type', { ascending: true });
    
    if (error) {
      console.error('Error fetching from event_availability view:', error);
      
      // Fallback: try to get events and calculate availability manually
      console.log('Trying fallback approach...');
      const { data: eventsData, error: eventsError } = await supabase!
        .from('events')
        .select('*')
        .order('event_type', { ascending: true });
      
      if (eventsError) {
        console.error('Error fetching events table:', eventsError);
        console.log('Using mock data as final fallback');
        return mockEvents;
      }
      
      // Calculate availability manually
      const eventsWithAvailability: EventAvailability[] = [];
      
      for (const event of eventsData || []) {
        const { data: registrations, error: regError } = await supabase!
          .from('contestant_registrations')
          .select('id')
          .eq('event_id', event.id)
          .gte('created_at', '2026-06-01T00:00:00.000Z');
        
        if (regError) {
          console.warn(`Error fetching registrations for event ${event.id}:`, regError);
        }
        
        const currentRegistrations = registrations?.length || 0;
        const isSlackEvent = event.event_type.toLowerCase().includes('slack');
        const availableSpots = isSlackEvent ? 999999 : event.max_capacity - currentRegistrations;
        const isFull = isSlackEvent ? false : currentRegistrations >= event.max_capacity;
        
        eventsWithAvailability.push({
          ...event,
          current_registrations: currentRegistrations,
          available_spots: availableSpots,
          is_full: isFull
        });
      }
      
      console.log('Fallback successful, returning calculated data:', eventsWithAvailability);
      return eventsWithAvailability;
    }
    
    console.log('Successfully fetched event availability:', data);
    return data || [];
  } catch (err) {
    console.error('Unexpected error in getAvailableEvents:', err);
    console.log('Using mock data due to unexpected error');
    return mockEvents;
  }
};

export const createContestantRegistration = async (registration: ContestantRegistration) => {
  // If no Supabase connection, simulate success for demo purposes
  if (!supabase || !hasSupabaseCredentials) {
    console.log('Supabase not available, simulating registration creation');
    await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API delay
    
    return {
      id: 'mock-id-' + Math.random().toString(36).substring(2, 11),
      confirmationNumber: Math.floor(1000000 + Math.random() * 9000000).toString(),
      teamEntryId: registration.is_team_event ? crypto.randomUUID() : null,
      teamSize: registration.is_team_event ? 2 : 1,
      message: registration.is_team_event 
        ? 'Demo: Team registration created successfully for both partners'
        : 'Demo: Registration created successfully'
    };
  }

  // Use the Edge Function to create the registration securely
  const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-contestant-registration`;
  
  const headers = {
    'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
    'Content-Type': 'application/json',
  };

  console.log('Calling Edge Function with data:', registration);

  try {
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(registration),
    });

    const responseText = await response.text();
    console.log('Edge Function response:', response.status, responseText);

    if (!response.ok) {
      let errorData;
      try {
        errorData = JSON.parse(responseText);
      } catch {
        errorData = { message: `HTTP ${response.status}: ${responseText}` };
      }
      
      throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = JSON.parse(responseText);
    console.log('Registration successful:', data);
    return data;
  } catch (error) {
    console.error('Error in createContestantRegistration:', error);
    throw error;
  }
};

export const getEventById = async (eventId: string): Promise<Event | null> => {
  if (!supabase || !hasSupabaseCredentials) {
    // Return mock event if Supabase not available
    const mockEvent = mockEvents.find(e => e.id === eventId);
    return mockEvent || null;
  }

  const { data, error } = await supabase
    .from('events')
    .select('*')
    .eq('id', eventId)
    .single();
  
  if (error) {
    console.error('Error fetching event:', error);
    throw new Error(`Failed to load event: ${error.message}`);
  }
  return data;
};

// Helper function to check if an event is a team event
export const isTeamEvent = (eventName: string): boolean => {
  return eventName.toLowerCase().includes('team roping');
};

// Helper function to check if an event is a Slack event (unlimited capacity)
export const isSlackEvent = (eventType: string): boolean => {
  return eventType.toLowerCase().includes('slack');
};

// --- Lineup ("First Shall Be Last") ---

export interface LineupContestant {
  id: string;
  event_id: string;
  first_name: string;
  last_name: string;
  registered_at: string;
  event_name?: string;
  event_type?: string;
}

export const getLineup = async (eventId?: string): Promise<LineupContestant[]> => {
  if (!supabase || !hasSupabaseCredentials) {
    return [];
  }

  let query = supabase
    .from('contestant_registrations')
    .select(`
      id,
      event_id,
      first_name,
      last_name,
      registered_at,
      events!inner (
        event_name,
        event_type
      )
    `)
    .gte('registered_at', '2026-06-01T00:00:00.000Z')
    .order('registered_at', { ascending: false });

  if (eventId) {
    query = query.eq('event_id', eventId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching lineup:', error);
    return [];
  }

  return (data || []).map((row: any) => ({
    id: row.id,
    event_id: row.event_id,
    first_name: row.first_name,
    last_name: row.last_name,
    registered_at: row.registered_at,
    event_name: row.events?.event_name,
    event_type: row.events?.event_type,
  }));
};

export const subscribeToLineup = (
  eventId: string | undefined,
  onUpdate: () => void
) => {
  if (!supabase) return { unsubscribe: () => {} };

  const channel = supabase
    .channel('lineup-changes')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'contestant_registrations',
        ...(eventId ? { filter: `event_id=eq.${eventId}` } : {}),
      },
      () => {
        onUpdate();
      }
    )
    .subscribe();

  return {
    unsubscribe: () => {
      supabase!.removeChannel(channel);
    },
  };
};